/**
 * Move a document, or a folder of them, to another project (RFC 0338 3f).
 *
 * A document is its own Automerge doc with a global id; a project only lists
 * it. So a move between projects lists it in the new project, points the
 * document's own `workspaceId` there, carries the index entries that let the
 * new project's lists find its comments, and then unlists it from the old
 * one. The id never changes, so links, panes and comments follow.
 *
 * What travels by itself since RFC 0339: the comments (in the document's own
 * comments doc, `commentsId`) and the pairings (on the document). What this
 * carries: the catalog entry and the old project's `commentIndex` entries for
 * the moved documents. A pairing's index entry stays where it is, on the
 * session's project, because it is the session's.
 *
 * Order matters for a reader that looks mid-move: listed in the new project
 * first, unlisted from the old one last, so there is never a moment when the
 * document is in neither.
 */

import type {
  AnyDocumentId,
  DocHandle,
  Repo,
} from '@automerge/automerge-repo/slim'
import { moveProjectComments } from '../../migrations'
import { installedApps } from '../apps/reads'
import {
  mapWithConflicts,
  removeCommentIndexEntries,
  writeCommentIndex,
} from '../comments/store'
import type { CommentIndexEntry } from '../comments/types'
import { changeProject } from '../project/scalars'
import type { ProjectDoc } from '../project/types'
import { worldProjectMounts } from '../world/reads'
import type { WorldDoc } from '../world/types'
import {
  assertDocumentOutsideSources,
  freeDocumentPath,
  normalizeDocumentPath,
} from './mutations'
import type { DocRef, DocumentDoc } from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any

export type MoveTarget =
  /** One document. `path` is where it lands in the new project; absent keeps
   *  the path it had. */
  | { documentId: string; path?: string }
  /** Every document under `folder/`. `to` is the folder they land under in the
   *  new project; absent keeps the folder's name and place. */
  | { folder: string; to?: string }
  /** Every document, optionally under a destination folder. Used when a
   * project is dissolved; root-level pages have no source folder prefix. */
  | { all: true; to?: string }

export interface MoveToProjectResult {
  /** Ids moved, each now listed in the new project only. */
  moved: string[]
  /** Data types of moved app records whose app neither the new project nor
   *  the World has.
   *  They list there but open only once the app is installed; the move does
   *  not install it. */
  missingApps: string[]
}

const storedPath = (path: unknown): string => {
  const p = String(path ?? '')
  return normalizeDocumentPath(p) ?? p
}

export async function moveToProject(
  repo: Repo,
  fromProjectId: string,
  toProjectId: string,
  target: MoveTarget,
  opts?: {
    /** The ids a caller checked first (an edit rule, say). A folder holding
     *  anything else by the time it moves refuses, as `moveFolder` does. */
    expectIds?: readonly string[]
    /** The World both projects are in: an app installed there counts as the
     *  new project's (RFC 0338 3g). */
    worldId?: string | null
    /** RFC 0357 S5: allow a dissolved project's own mount while moving ALL
     *  of its contents back to that mount's parent. Never ignores another
     *  project's boundary or a different destination. */
    dissolvingProjectId?: string
    /** RFC 0357 3i: land in a folder that is already there, keeping both of
     *  any clashing document under a free name and joining folder entries. */
    merge?: boolean
  },
): Promise<MoveToProjectResult> {
  if (fromProjectId === toProjectId) {
    throw new Error(
      'that is already this project; move it within the project instead',
    )
  }
  const from = await repo.find<ProjectDoc>(fromProjectId as AnyDocumentId)
  const to = await repo.find<ProjectDoc>(toProjectId as AnyDocumentId)
  // A project, not just any doc the repo can open: a page, a session or a
  // World passed here would get a catalog written into it and the moved
  // document would vanish from every real project.
  if (!isProjectDoc(to.doc())) {
    throw new Error(`${toProjectId} is not a project`)
  }

  // Comments still on the old project's own map (written before RFC 0339)
  // go onto their pages first, so none is left behind on a project the page
  // no longer belongs to.
  await moveProjectComments(repo, fromProjectId)

  const source = from.doc()?.documents ?? {}
  const moves = new Map<string, { ref: DocRef; path: string }>()
  const folderMoves = new Map<string, string>()
  if ('documentId' in target) {
    const ref = source[target.documentId]
    if (!ref) {
      throw new Error(`${target.documentId} is not a document in this project`)
    }
    const path =
      target.path !== undefined
        ? normalizeDocumentPath(target.path)
        : storedPath(ref.path)
    if (!path) throw new Error(`"${target.path}" is not a document path`)
    moves.set(target.documentId, { ref, path })
  } else if ('all' in target) {
    const dst = target.to === undefined ? '' : normalizeDocumentPath(target.to)
    if (dst === null) throw new Error(`"${target.to}" is not a folder path`)
    for (const [id, ref] of Object.entries(source)) {
      const path = storedPath(ref.path)
      moves.set(id, { ref, path: dst ? `${dst}/${path}` : path })
    }
    for (const [id, folder] of Object.entries(from.doc()?.folders ?? {})) {
      const path = storedPath(folder.path)
      folderMoves.set(id, dst ? `${dst}/${path}` : path)
    }
    if (moves.size === 0 && folderMoves.size === 0)
      return { moved: [], missingApps: [] }
  } else {
    const src = normalizeDocumentPath(target.folder)
    if (!src) throw new Error(`"${target.folder}" is not a folder path`)
    const dst =
      target.to !== undefined
        ? target.to === ''
          ? ''
          : normalizeDocumentPath(target.to)
        : src
    if (dst === null) throw new Error(`"${target.to}" is not a folder path`)
    const prefix = `${src}/`
    for (const [id, ref] of Object.entries(source)) {
      const p = storedPath(ref.path)
      if (p.startsWith(prefix)) {
        moves.set(id, {
          ref,
          path: dst
            ? `${dst}/${p.slice(prefix.length)}`
            : p.slice(prefix.length),
        })
      }
    }
    for (const [id, entry] of Object.entries(from.doc()?.folders ?? {})) {
      const path = storedPath(entry.path)
      if (path === src || path.startsWith(prefix)) {
        if (!dst && path === src) continue // Flatten into the destination root.
        folderMoves.set(
          id,
          dst ? `${dst}${path.slice(src.length)}` : path.slice(prefix.length),
        )
      }
    }
    if (moves.size === 0 && folderMoves.size === 0)
      throw new Error(`no documents or explicit folders under ${src}/`)
  }
  if (opts?.expectIds) {
    const expected = new Set(opts.expectIds)
    const same =
      expected.size === moves.size &&
      [...moves.keys()].every((id) => expected.has(id))
    if (!same)
      throw new Error(
        'the folder changed while the move was being checked; try again',
      )
  }

  // Nothing of these documents may be left on the old project's legacy maps:
  // a comment the migration could not place (its page's comments document
  // was not reachable) would stay readable only through the old project.
  const left = legacyLeftovers(from.doc(), new Set(moves.keys()))
  if (left > 0) {
    throw new Error(
      `${left} older comment${left === 1 ? '' : 's'} or pairing${left === 1 ? '' : 's'} on this document could not be moved onto it yet; try again once it has synced`,
    )
  }

  // All or nothing: every landing path is checked against the new project's
  // catalog, and against each other, before anything is written. A document
  // the new project already lists AT ITS LANDING PATH is an earlier move of
  // this one that did not finish, and the move resumes it.
  const destDocs = to.doc()?.documents ?? {}
  const destFolders = to.doc()?.folders ?? {}
  const worldDoc = opts?.worldId
    ? (await repo.find<WorldDoc>(opts.worldId as AnyDocumentId)).doc()
    : undefined
  const projectMounts = worldProjectMounts(worldDoc).filter(
    (mount) => mount.parentProjectId === toProjectId,
  )
  if (opts?.dissolvingProjectId) {
    const ownMount = projectMounts.find(
      (mount) => mount.projectId === opts.dissolvingProjectId,
    )
    if (
      !('all' in target) ||
      opts.dissolvingProjectId !== fromProjectId ||
      !ownMount ||
      normalizeDocumentPath(target.to ?? '') !== ownMount.folder
    )
      throw new Error(
        'only a project moving into its own parent mount may dissolve',
      )
  }
  const projectMountPaths = projectMounts
    .filter((mount) => mount.projectId !== opts?.dissolvingProjectId)
    .map((mount) => mount.folder)
  const assertOutsideProjectMounts = (path: string) => {
    if (
      projectMountPaths.some(
        (mount) => path === mount || path.startsWith(`${mount}/`),
      )
    )
      throw new Error(`${path} belongs to a project there`)
  }
  const taken = new Map<string, string>()
  for (const [id, ref] of Object.entries(destDocs))
    taken.set(storedPath(ref.path), id)
  const takenFolders = new Map<string, string>()
  for (const [id, folder] of Object.entries(destFolders))
    takenFolders.set(storedPath(folder.path), id)
  // A folder holds its path as surely as a document does (one owner per
  // path, 3i): explicit entries there or landing, and every folder a
  // document there sits in. A document may not be named onto one.
  const folderPaths = new Set([...takenFolders.keys(), ...folderMoves.values()])
  for (const path of taken.keys()) {
    for (let i = path.indexOf('/'); i > 0; i = path.indexOf('/', i + 1))
      folderPaths.add(path.slice(0, i))
  }
  const landing = new Map<string, string>()
  for (const [id, m] of moves) {
    // Merging keeps both: a taken path becomes a free one beside it. A
    // document already listed there (a move resumed) keeps its own path.
    if (opts?.merge && !destDocs[id]) {
      const holder = taken.get(m.path) ?? landing.get(m.path)
      if ((holder && holder !== id) || folderPaths.has(m.path))
        m.path = freeDocumentPath(
          m.path,
          new Set([...taken.keys(), ...folderPaths, ...landing.keys()]),
        )
    }
    assertDocumentOutsideSources(to.doc(), m.path)
    assertOutsideProjectMounts(m.path)
    const listed = destDocs[id]
    if (listed && storedPath(listed.path) !== m.path) {
      throw new Error(
        `${id} is already in that project at ${storedPath(listed.path)}`,
      )
    }
    // Two documents being moved with one path is a clash inside the folder,
    // not with the destination: said apart, so the window can say which.
    const twin = landing.get(m.path)
    if (twin && twin !== id) {
      throw new Error(`${twin} and ${id} would both move to ${m.path}`)
    }
    const holder = taken.get(m.path)
    if (holder && holder !== id) {
      throw new Error(`${m.path} is already taken there (${holder})`)
    }
    if (folderPaths.has(m.path))
      throw new Error(`${m.path} is already a folder there`)
    landing.set(m.path, id)
  }
  const folderLanding = new Set<string>()
  const joinedFolders = new Set<string>()
  for (const [id, path] of folderMoves) {
    if (!path || !normalizeDocumentPath(path))
      throw new Error(`"${path}" is not a folder path`)
    assertDocumentOutsideSources(to.doc(), `${path}/file`)
    assertOutsideProjectMounts(path)
    if (taken.has(path) || landing.has(path))
      throw new Error(`${path} is already a document there`)
    const existing = takenFolders.get(path)
    if (existing && existing !== id && opts?.merge) {
      // Joins the folder already there: no second entry at one path.
      joinedFolders.add(id)
      continue
    }
    if ((existing && existing !== id) || folderLanding.has(path))
      throw new Error(`${path} is already a folder there`)
    if (destFolders[id] && storedPath(destFolders[id].path) !== path)
      throw new Error(`${id} is already a folder at ${destFolders[id].path}`)
    folderLanding.add(path)
  }

  // Every document must be reachable before anything is written: its own
  // workspaceId is what routes a later comment's index, so a move that could
  // not write it would leave comments indexing into the old project.
  const handles = new Map<string, DocHandle<DocumentDoc>>()
  for (const id of moves.keys()) {
    const doc = await repo.find<DocumentDoc>(id as AnyDocumentId)
    if (!doc.doc()) throw new Error(`${id} could not be opened; try again`)
    handles.set(id, doc)
  }

  // 1. Listed in the new project.
  changeProject(to, (d: Draft) => {
    if (!d.documents) d.documents = {}
    if (folderMoves.size > 0 && !d.folders) d.folders = {}
    for (const [id, path] of folderMoves) {
      if (joinedFolders.has(id)) continue
      if (!d.folders[id]) d.folders[id] = { path }
    }
    for (const [id, m] of moves) {
      if (d.documents[id]) continue
      const entry: Record<string, unknown> = {
        title: String(m.ref.title ?? m.path),
        path: m.path,
        mime: String(m.ref.mime ?? ''),
        updatedAt: Number(m.ref.updatedAt) || Date.now(),
      }
      if (m.ref.dataType) entry.dataType = String(m.ref.dataType)
      d.documents[id] = entry
    }
  })

  // 2. Each document names its new project. Its comments and pairings are on
  //    it already, so they come with it. Not caught: a failure here stops the
  //    move with the old project still listing the document, and running the
  //    same move again finishes it.
  for (const [id, m] of moves) {
    handles.get(id)?.change((d) => {
      d.workspaceId = toProjectId
      d.path = m.path
    })
  }

  // 3. The index entries that let the new project's lists find the comments,
  //    then out of every copy of the old project's index.
  const oldIndex = mapWithConflicts<CommentIndexEntry>(
    from.doc(),
    'commentIndex',
  )
  const carried: Record<string, CommentIndexEntry> = {}
  for (const [commentId, entry] of Object.entries(oldIndex)) {
    if (entry.documentId && moves.has(String(entry.documentId))) {
      carried[commentId] = plainEntry(entry)
    }
  }
  await writeCommentIndex(repo, toProjectId, carried)
  removeCommentIndexEntries(from, Object.keys(carried))

  // 4. Unlisted from the old project, last.
  changeProject(from, (d: Draft) => {
    for (const id of moves.keys()) {
      if (d.documents?.[id]) delete d.documents[id]
    }
    for (const id of folderMoves.keys()) {
      if (d.folders?.[id]) delete d.folders[id]
    }
  })

  const installed = new Set(
    installedApps(worldDoc, to.doc()).map((a) => String(a.dataType)),
  )
  const missingApps = [
    ...new Set(
      [...moves.values()]
        .map((m) => (m.ref.dataType ? String(m.ref.dataType) : ''))
        .filter((t) => t && !installed.has(t)),
    ),
  ]
  return { moved: [...moves.keys()], missingApps }
}

/** A project doc, by shape: it keeps sessions, and is not a page. */
export function isProjectDoc(doc: unknown): boolean {
  if (!doc || typeof doc !== 'object') return false
  const d = doc as Record<string, unknown>
  return (
    Array.isArray(d.sessionIds) &&
    d.workspaceId === undefined &&
    d.mime === undefined
  )
}

/** Entries on the old project's legacy maps (RFC 0339 3e) still about one of
 *  `ids`: comments on the page, and pairings to it. */
function legacyLeftovers(doc: unknown, ids: ReadonlySet<string>): number {
  const d = (doc ?? {}) as {
    comments?: Record<string, { target?: { documentId?: unknown } }>
    pairings?: Record<string, { documentId?: unknown }>
  }
  let n = 0
  for (const c of Object.values(d.comments ?? {})) {
    if (ids.has(String(c?.target?.documentId ?? ''))) n++
  }
  for (const p of Object.values(d.pairings ?? {})) {
    if (ids.has(String(p?.documentId ?? ''))) n++
  }
  return n
}

/** An index entry copied as plain scalars, so nothing of the old project's
 *  Automerge objects is written into the new one. */
function plainEntry(e: CommentIndexEntry): CommentIndexEntry {
  return {
    ...(e.documentId ? { documentId: String(e.documentId) } : {}),
    ...(e.sessionId ? { sessionId: String(e.sessionId) } : {}),
    ...(e.anchorText ? { anchorText: String(e.anchorText) } : {}),
    ...(e.parentId ? { parentId: String(e.parentId) } : {}),
    author: String(e.author ?? ''),
    ...(e.authorSessionId
      ? { authorSessionId: String(e.authorSessionId) }
      : {}),
    resolved: e.resolved === true,
    createdAt: Number(e.createdAt ?? 0),
  }
}
